// src/layouts/MainLayout.tsx
import React, { useState } from "react";
import { Link, Outlet, useNavigate } from "react-router-dom";
import {
  Menu,
  X,
  LogIn,
  UserPlus,
  Grid,
  LogOut,
  ShieldCheck,
} from "lucide-react";
import { clearCredentials } from "../slices/authSlice";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { useLogoutMutation } from "../services/auth";

/**
 * MainLayout - Public layout for homepage, sales pages, login, register, etc.
 * Does NOT include dashboard sidebar - that's in DashboardLayout
 */
const MainLayout: React.FC = () => {
  const [open, setOpen] = useState(false); // mobile menu
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [logoutApi] = useLogoutMutation();

  const auth = useAppSelector((s) => s.auth);
  const authenticated = Boolean(auth?.token);
  const role = auth?.role || "";
  const isAdmin = role === "admin";

  async function handleLogout() {
    try {
      await logoutApi(undefined).unwrap();
    } catch (err) {
      // ignore server errors
    } finally {
      dispatch(clearCredentials());
      navigate("/");
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 text-slate-900">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Brand */}
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-md bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white font-bold">
                  D
                </div>
                <div className="hidden sm:block">
                  <div className="font-semibold">Digital Income Blueprint</div>
                  <div className="text-xs text-slate-500">
                    Exclusive training platform
                  </div>
                </div>
              </Link>
            </div>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-4">
              {!authenticated ? (
                <>
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-2 px-3 py-2 rounded hover:bg-gray-100"
                  >
                    <LogIn size={16} /> Login
                  </Link>
                  <Link
                    to="/sl"
                    className="inline-flex items-center gap-2 bg-teal-400 text-[#042027] px-3 py-2 rounded font-medium hover:opacity-95"
                  >
                    <UserPlus size={16} /> Enroll Now
                  </Link>
                </>
              ) : (
                <>
                  {!isAdmin && (
                    <Link
                      to="/dashboard"
                      className="inline-flex items-center gap-2 px-3 py-2 rounded hover:bg-gray-100"
                    >
                      <Grid size={16} /> Dashboard
                    </Link>
                  )}

                  {isAdmin && (
                    <Link
                      to="/admin"
                      className="inline-flex items-center gap-2 px-3 py-2 rounded hover:bg-gray-100"
                    >
                      <ShieldCheck size={16} /> Admin Panel
                    </Link>
                  )}

                  <button
                    onClick={handleLogout}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded hover:bg-gray-100"
                  >
                    <LogOut size={16} /> Logout
                  </button>
                </>
              )}
            </nav>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center gap-2">
              <button
                onClick={() => setOpen((s) => !s)}
                className="p-2 rounded-md hover:bg-gray-100"
                aria-label="Toggle menu"
              >
                {open ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile nav dropdown */}
        {open && (
          <div className="md:hidden border-t bg-white">
            <div className="px-4 py-3 space-y-2">
              {!authenticated ? (
                <>
                  <Link
                    to="/login"
                    className="flex items-center gap-2 px-3 py-2 rounded hover:bg-gray-100"
                  >
                    <LogIn size={16} /> Login
                  </Link>
                  <Link
                    to="/sl"
                    className="flex items-center gap-2 px-3 py-2 rounded bg-teal-400 text-[#042027] font-medium"
                  >
                    <UserPlus size={16} /> Enroll Now
                  </Link>
                </>
              ) : (
                <>
                  {!isAdmin && (
                    <Link
                      to="/dashboard"
                      className="flex items-center gap-2 px-3 py-2 rounded hover:bg-gray-100"
                    >
                      <Grid size={16} /> Dashboard
                    </Link>
                  )}

                  {isAdmin && (
                    <Link
                      to="/admin"
                      className="flex items-center gap-2 px-3 py-2 rounded hover:bg-gray-100"
                    >
                      <ShieldCheck size={16} /> Admin Panel
                    </Link>
                  )}

                  <button
                    onClick={() => {
                      handleLogout();
                      setOpen(false);
                    }}
                    className="flex items-center gap-2 px-3 py-2 rounded hover:bg-gray-100 w-full text-left"
                  >
                    <LogOut size={16} /> Logout
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Main content area */}
      <div className="flex-1">
        <Outlet />
      </div>

      {/* Footer */}
      <footer className="bg-white border-t">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="text-sm text-slate-600">
            © {new Date().getFullYear()} Digital Income Blueprint
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/terms"
              className="text-sm text-slate-500 hover:underline"
            >
              Terms
            </Link>
            <Link
              to="/privacy"
              className="text-sm text-slate-500 hover:underline"
            >
              Privacy
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MainLayout;
